import { _ActionsTree } from 'pinia';
import { ICarSelectStoreState } from '../../interfaces/ICarSelectStoreState';
import { IStateModelData } from '../../interfaces/IStateModelData';

export const carAdvisoryActions: Record<string, any> = {
  /**
   * Utility action to update a select property in the store with the given list
   *
   * @param list Items that need to replace the select property list
   * @param stateToUpdate The select property that is to be updated
   */
  updateStateWithList(list: any, stateToUpdate: keyof ICarSelectStoreState) {
    this[stateToUpdate] = list;
  },

  /**
   * Removes the make and any associated models and badges from the appropriate select properties
   *
   * @param makeToRemove The name of the make to remove
   */
  removeMakeAndRelatedModels(makeToRemove: string) {
    if (makeToRemove) {
      this.$patch((state: ICarSelectStoreState) => {
        state.selectedMakes = state.selectedMakes.filter((make) => make !== makeToRemove);
        state.selectedModels = state.selectedModels.filter((model) => model.makeName !== makeToRemove);
        state.selectedBadges = state.selectedBadges.filter((badge) => badge.makeName !== makeToRemove);
      });
    }
  },
  /**
   * the flag is updated only when results on the main page are to be updated.
   * @param resultsUpdated if the results are updated.
   */
  toggleResultsUpdated(resultsUpdated: boolean) {
    this.resultsUpdated = resultsUpdated;
  },
  /**
   * Adds car models to the store for the given car make.
   *
   * @param make The name of the car make
   */
  addMakeAndRelatedModels(make: string) {
    this.$patch((state: ICarSelectStoreState) => {
      if (!state.selectedMakes.includes(make)) state.selectedMakes.push(make);
      this.addMakeRelatedModels(make);
    });
  },

  /**
   * Replaces the selected car makes with the new list and updates the selected models and badges with only those from the new make list
   *
   * @param makeList List of car makes
   */
  addRemoveSelectedMakes(makeList: string[]) {
    this.$patch((state: ICarSelectStoreState) => {
      const makesToRemove = state.selectedMakes.filter((makeName) => !makeList.includes(makeName));

      state.selectedMakes = makeList;
      state.selectedModels = state.selectedModels.filter((model) => !makesToRemove.includes(model.makeName));
      state.selectedBadges = state.selectedBadges.filter((badge) => !makesToRemove.includes(badge.makeName));
    });
  },

  /**
   * Removes the model, and associated badges for the model, from the list
   *
   * @param model The model that was removed
   */
  removeMakeModelAndRelatedBadges(model: IStateModelData) {
    if (model) {
      this.$patch((state: ICarSelectStoreState) => {
        const indexOfModel = state.selectedModels.findIndex((modelObject) => {
          return modelObject.makeName === model.makeName && modelObject.modelName === model.modelName;
        });

        if (indexOfModel >= 0) {
          state.selectedModels.splice(indexOfModel, 1);
        }

        state.selectedBadges = state.selectedBadges.filter((badge) => model.makeName !== badge.makeName || model.modelName !== badge.modelName);
      });
    }
  },

  /**
   * Replaces the selected model list with the model list passed in and updates the badges to only retain badges for models in the list.
   *
   * @param newModelList The new list of models to replace the currently selected models
   */
  addRemoveSelectedModels(newModelList: IStateModelData[]) {
    this.$patch((state: ICarSelectStoreState) => {
      const modelsToRemove = state.selectedModels.filter(
        (selectedModelItem) =>
          newModelList.findIndex(
            (newModelItem: IStateModelData) => newModelItem.makeName === selectedModelItem.makeName && newModelItem.modelName === selectedModelItem.modelName
          ) < 0
      );
      state.selectedModels = newModelList;

      state.selectedBadges = state.selectedBadges.filter(
        (badge) => modelsToRemove.findIndex((modelItem) => modelItem.makeName === badge.makeName && modelItem.modelName === badge.modelName) < 0
      );
    });
  },
};
